import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './HeroSection.css';

const HeroSection = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);

  // 滚动交互状态管理
  const [currentLayer, setCurrentLayer] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);

  // About TAG按钮悬停状态管理
  const [isAboutButtonHovered, setIsAboutButtonHovered] = useState(false);

  // 使用 useRef 来存储锁定状态，确保事件处理器能获取最新值
  const isAnimationLockedRef = useRef(false);

  const handleAboutClick = () => {
    navigate('/about');
  };

  const handleAboutButtonMouseEnter = () => {
    // 只有当按钮可见时才允许悬停效果
    if (currentLayer >= 3) {
      setIsAboutButtonHovered(true);
    }
  };

  const handleAboutButtonMouseLeave = () => {
    setIsAboutButtonHovered(false);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationId;

    // 鼠标位置追踪
    let mouseX = 0;
    let mouseY = 0;
    let isMouseOver = false;

    // 设置canvas尺寸
    function resizeCanvas() {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    }
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // 鼠标事件监听
    const handleMouseMove = e => {
      const rect = canvas.getBoundingClientRect();
      mouseX = e.clientX - rect.left;
      mouseY = e.clientY - rect.top;
    };

    const handleMouseEnter = () => {
      isMouseOver = true;
    };

    const handleMouseLeave = () => {
      isMouseOver = false;
    };

    // 为整个section添加鼠标事件监听，确保覆盖文字区域
    const section = canvas.parentElement.parentElement;

    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseenter', handleMouseEnter);
    canvas.addEventListener('mouseleave', handleMouseLeave);

    // 为section添加鼠标事件，确保文字区域也能触发
    section.addEventListener('mousemove', handleMouseMove);
    section.addEventListener('mouseenter', handleMouseEnter);
    section.addEventListener('mouseleave', handleMouseLeave);

    // 滚轮事件监听
    const handleWheel = e => {
      // 使用 ref.current 获取最新的锁定状态
      if (isAnimationLockedRef.current) {
        return; // 不阻止默认滚动行为，允许正常页面滚动
      }

      e.preventDefault(); // 只在未锁定时阻止默认滚动

      if (isScrolling) return;

      setIsScrolling(true);

      if (e.deltaY > 0) {
        // 向下滚动
        setCurrentLayer(prev => {
          const newLayer = Math.min(prev + 1, 3);
          // 当到达最后一层时，立即锁定动画
          if (newLayer === 3) {
            isAnimationLockedRef.current = true; // 使用 ref 设置锁定状态
          }
          return newLayer;
        });
      } else {
        // 向上滚动 - 只在未锁定时允许
        if (!isAnimationLockedRef.current) {
          setCurrentLayer(prev => Math.max(prev - 1, 0));
        }
      }

      // 防抖处理
      setTimeout(() => {
        setIsScrolling(false);
      }, 300);
    };

    section.addEventListener('wheel', handleWheel, { passive: false });

    // 创建正十二面体的顶点
    function createDodecahedronVertices() {
      const phi = (1 + Math.sqrt(5)) / 2;
      const vertices = [];

      // 添加所有20个顶点
      for (let i = -1; i <= 1; i += 2) {
        for (let j = -1; j <= 1; j += 2) {
          for (let k = -1; k <= 1; k += 2) {
            vertices.push([i, j, k]);
          }
        }
      }

      for (let i = -1; i <= 1; i += 2) {
        for (let j = -1; j <= 1; j += 2) {
          vertices.push([0, i / phi, j * phi]);
        }
      }

      for (let i = -1; i <= 1; i += 2) {
        for (let j = -1; j <= 1; j += 2) {
          vertices.push([i / phi, j * phi, 0]);
        }
      }

      for (let i = -1; i <= 1; i += 2) {
        for (let j = -1; j <= 1; j += 2) {
          vertices.push([i * phi, 0, j / phi]);
        }
      }

      return vertices;
    }

    const vertices = createDodecahedronVertices();

    // 计算边
    function createDodecahedronEdges(vertices) {
      const edges = [];
      const edgeLength = 2 / ((1 + Math.sqrt(5)) / 2);
      const tolerance = 0.01;

      for (let i = 0; i < vertices.length; i++) {
        for (let j = i + 1; j < vertices.length; j++) {
          const dist = Math.sqrt(
            Math.pow(vertices[i][0] - vertices[j][0], 2) +
              Math.pow(vertices[i][1] - vertices[j][1], 2) +
              Math.pow(vertices[i][2] - vertices[j][2], 2)
          );

          if (Math.abs(dist - edgeLength) < tolerance) {
            edges.push([i, j]);
          }
        }
      }

      return edges;
    }

    const edges = createDodecahedronEdges(vertices);

    // 两个十二面体的独立旋转状态
    let rotation1 = { x: 0, y: 0, z: 0 };
    let rotation2 = { x: Math.PI / 4, y: Math.PI / 3, z: Math.PI / 6 };

    function project3D(vertex, rotation, scale, offsetX = 0, offsetY = 0) {
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;

      let [x, y, z] = vertex;

      // X轴旋转
      const cosX = Math.cos(rotation.x);
      const sinX = Math.sin(rotation.x);
      let y1 = y * cosX - z * sinX;
      let z1 = y * sinX + z * cosX;

      // Y轴旋转
      const cosY = Math.cos(rotation.y);
      const sinY = Math.sin(rotation.y);
      let x2 = x * cosY + z1 * sinY;
      let z2 = -x * sinY + z1 * cosY;

      // Z轴旋转
      const cosZ = Math.cos(rotation.z);
      const sinZ = Math.sin(rotation.z);
      let x3 = x2 * cosZ - y1 * sinZ;
      let y3 = x2 * sinZ + y1 * cosZ;

      // 透视投影
      const distance = 5;
      const factor = distance / (distance + z2);

      return {
        x: centerX + offsetX + x3 * scale * factor,
        y: centerY + offsetY + y3 * scale * factor,
        depth: z2,
      };
    }

    function drawDodecahedron(
      rotation,
      scale,
      strokeStyle,
      lineWidth,
      alpha,
      offsetX = 0,
      offsetY = 0
    ) {
      ctx.strokeStyle = strokeStyle;
      ctx.lineWidth = lineWidth;
      ctx.globalAlpha = alpha;

      edges.forEach(edge => {
        const p1 = project3D(
          vertices[edge[0]],
          rotation,
          scale,
          offsetX,
          offsetY
        );
        const p2 = project3D(
          vertices[edge[1]],
          rotation,
          scale,
          offsetX,
          offsetY
        );

        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
        ctx.stroke();
      });
    }

    // 随机性变化
    let wobble = {
      x: Math.random() * 0.002 + 0.001,
      y: Math.random() * 0.002 + 0.001,
      z: Math.random() * 0.002 + 0.001,
    };

    function updateWobble() {
      if (Math.random() < 0.01) {
        wobble.x = Math.random() * 0.004 - 0.002 + 0.002;
        wobble.y = Math.random() * 0.004 - 0.002 + 0.002;
        wobble.z = Math.random() * 0.004 - 0.002 + 0.002;
      }
    }

    function draw() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (isMouseOver) {
        // 根据鼠标距离计算透明度
        const maxDistance = 400;

        // 绘制多个十二面体，根据距离调整透明度
        function drawWithFade(rotation, scale, offsetX, offsetY, baseAlpha) {
          const dx = mouseX - (canvas.width / 2 + offsetX);
          const dy = mouseY - (canvas.height / 2 + offsetY);
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < maxDistance) {
            const fade = 1 - distance / maxDistance;
            const alpha = baseAlpha * fade;
            drawDodecahedron(
              rotation,
              scale,
              '#6b7280',
              1,
              alpha,
              offsetX,
              offsetY
            );
          }
        }

        // 中间的十二面体
        drawWithFade(rotation2, 400, 0, 0, 0.3);
        drawWithFade(rotation1, 250, 0, 0, 0.4);

        // 左边的十二面体
        drawWithFade(rotation2, 400, -600, 0, 0.3);
        drawWithFade(rotation1, 250, -600, 0, 0.4);

        // 右边的十二面体
        drawWithFade(rotation2, 400, 600, 0, 0.3);
        drawWithFade(rotation1, 250, 600, 0, 0.4);

        // 左上角的十二面体
        drawWithFade(rotation2, 350, -500, -200, 0.25);
        drawWithFade(rotation1, 200, -500, -200, 0.35);

        // 右上角的十二面体
        drawWithFade(rotation2, 350, 500, -200, 0.25);
        drawWithFade(rotation1, 200, 500, -200, 0.35);

        // 左下角的十二面体
        drawWithFade(rotation2, 350, -500, 200, 0.25);
        drawWithFade(rotation1, 200, -500, 200, 0.35);

        // 右下角的十二面体
        drawWithFade(rotation2, 350, 500, 200, 0.25);
        drawWithFade(rotation1, 200, 500, 200, 0.35);

        // 四个角落边缘的十二面体
        // 左上角边缘
        drawWithFade(rotation2, 300, -800, -300, 0.2);
        drawWithFade(rotation1, 180, -800, -300, 0.3);

        // 左下角边缘
        drawWithFade(rotation2, 300, -800, 300, 0.2);
        drawWithFade(rotation1, 180, -800, 300, 0.3);

        // 右上角边缘
        drawWithFade(rotation2, 300, 800, -300, 0.2);
        drawWithFade(rotation1, 180, 800, -300, 0.3);

        // 右下角边缘
        drawWithFade(rotation2, 300, 800, 300, 0.2);
        drawWithFade(rotation1, 180, 800, 300, 0.3);
      }

      // 更新旋转角度
      rotation1.x += 0.003;
      rotation1.y += 0.005;
      rotation1.z += 0.002;

      // 外层随机旋转
      updateWobble();
      rotation2.x += wobble.x;
      rotation2.y -= wobble.y;
      rotation2.z += wobble.z * 1.5;

      rotation2.x += Math.sin(Date.now() * 0.0001) * 0.001;
      rotation2.y += Math.cos(Date.now() * 0.00015) * 0.001;

      animationId = requestAnimationFrame(draw);
    }

    draw();

    // 清理函数
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseenter', handleMouseEnter);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
      section.removeEventListener('mousemove', handleMouseMove);
      section.removeEventListener('mouseenter', handleMouseEnter);
      section.removeEventListener('mouseleave', handleMouseLeave);
      section.removeEventListener('wheel', handleWheel);
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, []);

  return (
    <section
      className='pt-0 pb-36 bg-cover bg-center bg-no-repeat min-h-[calc(75vh+4rem)] flex items-center relative overflow-hidden'
      style={{ backgroundImage: 'url(/background-image.png)' }}
    >
      {/* 背景圖片透明度覆蓋層 */}
      <div
        className={`absolute inset-0 bg-white transition-opacity duration-500 ease-in-out ${
          isAboutButtonHovered ? 'bg-opacity-0' : 'bg-opacity-85'
        }`}
      ></div>
      {/* 12面體背景 */}
      <div className='dodecahedron-container'>
        <canvas ref={canvasRef} id='dodecahedron-bg'></canvas>
      </div>

      {/* 滚动交互文字内容 - 增加顶部padding来占据导航栏空间 */}
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center w-full relative z-10 bg-transparent pointer-events-none pt-20'>
        <div className='flex flex-col items-center justify-center space-y-4 bg-transparent'>
          {/* 第一层：One Platform */}
          <div
            className={`transition-all duration-700 ease-in-out bg-transparent ${
              currentLayer >= 0
                ? 'opacity-100 transform translate-y-0'
                : 'opacity-0 transform translate-y-4'
            }`}
          >
            <h1
              className={`text-4xl md:text-5xl lg:text-6xl font-light leading-tight transition-colors duration-500 ease-in-out ${
                isAboutButtonHovered ? 'text-white' : 'text-gray-600'
              }`}
            >
              ONE PLATFORM
            </h1>
          </div>

          {/* 第二层：All Students */}
          <div
            className={`transition-all duration-700 ease-in-out bg-transparent ${
              currentLayer >= 1
                ? 'opacity-100 transform translate-y-0'
                : 'opacity-0 transform translate-y-4'
            }`}
          >
            <h1
              className={`font-black text-4xl md:text-5xl lg:text-6xl leading-tight transition-colors duration-500 ease-in-out ${
                isAboutButtonHovered ? 'text-white' : 'text-gray-900'
              }`}
            >
              ALL STUDENTS
            </h1>
          </div>

          {/* 第三层：Across All Majors */}
          <div
            className={`transition-all duration-700 ease-in-out bg-transparent ${
              currentLayer >= 2
                ? 'opacity-100 transform translate-y-0'
                : 'opacity-0 transform translate-y-4'
            }`}
          >
            <h1
              className={`font-black text-4xl md:text-5xl lg:text-6xl leading-tight transition-colors duration-500 ease-in-out ${
                isAboutButtonHovered ? 'text-white' : 'text-gray-900'
              }`}
            >
              ACROSS ALL MAJORS
            </h1>
          </div>

          {/* 第四层：副标题和按钮 */}
          <div
            className={`transition-all duration-700 ease-in-out bg-transparent ${
              currentLayer >= 3
                ? 'opacity-100 transform translate-y-0'
                : 'opacity-0 transform translate-y-4'
            }`}
          >
            <p
              className={`text-base md:text-lg mb-10 max-w-3xl mx-auto leading-relaxed transition-colors duration-500 ease-in-out ${
                isAboutButtonHovered ? 'text-white' : 'text-gray-600'
              }`}
            >
              A bridge for art students to showcase, connect, and collaborate
            </p>
            <button
              onClick={handleAboutClick}
              onMouseEnter={handleAboutButtonMouseEnter}
              onMouseLeave={handleAboutButtonMouseLeave}
              className='bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-3 rounded-md text-lg font-medium border border-transparent hover:shadow-lg hover:shadow-xl hover:transform hover:-translate-y-1 transition-all duration-200 pointer-events-auto'
            >
              About TAG
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
